import clsx from "clsx"
import { useState } from "react"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import { faIcon } from "@utils/faIcon"

import AccessCheck from "@particles/AccessCheck/AccessCheck"

import { Icon } from "@atoms"

import { BottomSheet } from "@organisms"

import { CLIENTS_ROUTES } from "@routes/clients"
import { ESTIMATES_ROUTES } from "@routes/estimates"
import { INVOICES_ROUTES } from "@routes/invoices"
import { JOBS_ROUTES } from "@routes/jobs"
import { PRICEBOOK_ITEMS_ROUTES } from "@routes/pricebook-items"
import { PURCHASE_ORDER_ROUTES } from "@routes/purchase-orders"
import { VENDOR_ROUTES } from "@routes/vendor"

import { APIDeckTaxStrategies, AccountingIntegrations } from "@constants/choices"
import { objectMap } from "@constants/objectMap"
import { PERMISSION_LEVEL } from "@constants/permissionLevel"

import styles from "./BottomBarCreateButton.module.scss"
import { BottomBarCreateButtonProps } from "./BottomBarCreateButton.types"

export default function BottomBarCreateButton(props: BottomBarCreateButtonProps) {
    const { isDisabled = false, _isFocused = false, _isHovered = false, isPressed = false } = props

    const { user } = useUser()

    const isUsingTaxRates =
        user?.service_company?.use_taxes &&
        !(
            user?.service_company?.accounting_integration === AccountingIntegrations.apideck &&
            user?.service_company?.apideck_tax_strategy !== APIDeckTaxStrategies.normal
        )

    const [isBottomSheetOpen, setIsBottomSheetOpen] = useState<boolean>(false)
    const [isSubItemsOpen, setIsSubItemsOpen] = useState<boolean>(false)

    const onCloseBottomSheet = () => {
        setIsBottomSheetOpen(false)
        setTimeout(() => {
            closeSubItems()
        }, 300)
    }

    const openBottomSheet = () => {
        setIsBottomSheetOpen(true)
    }

    const openSubItems = () => {
        setIsSubItemsOpen(true)
    }

    const closeSubItems = () => {
        setIsSubItemsOpen(false)
    }

    const [
        estimateCreateRoute,
        jobsCreateRoute,
        invoicesCreateRoute,
        purchaseOrdersCreateRoute,
        vendorsCreateRoute,
        clientsCreateRoute,
        pricebookServiceCreateRoute,
        pricebookPartOrMaterialCreateRoute,
        pricebookMiscellaneousCreateRoute,
        pricebookDiscountCreateRoute,
        pricebookTaxRatesCreateRoute,
    ] = useSlugExtractor([
        ESTIMATES_ROUTES.CREATE,
        JOBS_ROUTES.CREATE,
        INVOICES_ROUTES.CREATE,
        PURCHASE_ORDER_ROUTES.CREATE,
        VENDOR_ROUTES.CREATE,
        CLIENTS_ROUTES.CREATE,
        PRICEBOOK_ITEMS_ROUTES.SERVICE.CREATE,
        PRICEBOOK_ITEMS_ROUTES.PART_OR_MATERIAL.CREATE,
        PRICEBOOK_ITEMS_ROUTES.MISCELLANEOUS.CREATE,
        PRICEBOOK_ITEMS_ROUTES.DISCOUNT.CREATE,
        PRICEBOOK_ITEMS_ROUTES.TAX_RATES.CREATE,
    ])

    return (
        <>
            <div className={styles.base}>
                <button
                    className={clsx(styles.trigger, {
                        [styles.isDisabled]: isDisabled,
                        [styles.focused]: _isFocused,
                        [styles.hovered]: _isHovered,
                        [styles.pressed]: isPressed,
                    })}
                    aria-disabled={isDisabled}
                    aria-label="Create Object"
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                        if (isDisabled) {
                            event.preventDefault()
                            event.stopPropagation()
                        } else {
                            openBottomSheet()
                        }
                    }}
                >
                    <Icon icon={faIcon({ name: "plus", style: "solid" })} size={16} />
                </button>
            </div>
            <BottomSheet.Root isOpen={isBottomSheetOpen} onClose={onCloseBottomSheet}>
                <BottomSheet.Content
                    header={{
                        title: "Create Object",
                    }}
                    priority="primary"
                    isVisible={!isSubItemsOpen}
                    itemsClassName={styles.items}
                >
                    {(isTabbable) => (
                        <>
                            <AccessCheck
                                entitlements={["entitlement_estimates_enabled"]}
                                permissions={{
                                    estimates_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.estimate.icon}
                                    path={estimateCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.estimate.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                entitlements={["entitlement_jobs_enabled"]}
                                permissions={{
                                    jobs_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.job.icon}
                                    path={jobsCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.job.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                entitlements={["entitlement_invoices_enabled"]}
                                permissions={{
                                    invoices_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.invoice.icon}
                                    path={invoicesCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.invoice.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                entitlements={["entitlement_purchase_orders_enabled"]}
                                permissions={{
                                    purchase_orders_create_permission: PERMISSION_LEVEL.RESTRICTED,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.purchase_order.icon}
                                    path={purchaseOrdersCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.purchase_order.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                permissions={{
                                    vendors_create_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.vendor.icon}
                                    path={vendorsCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.vendor.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                permissions={{
                                    clients_create_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.client.icon}
                                    path={clientsCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.client.label}
                                    isTabbable={isTabbable}
                                />
                            </AccessCheck>
                            <AccessCheck
                                permissions={{
                                    pricebook_create_permission: PERMISSION_LEVEL.FULL,
                                }}
                            >
                                <BottomSheet.Item
                                    icon={objectMap.pricebook_item.icon}
                                    className={styles.bottomSheetItem}
                                    label={objectMap.pricebook_item.label}
                                    isTabbable={isTabbable}
                                    hasSubItems={true}
                                    onClick={openSubItems}
                                />
                            </AccessCheck>
                        </>
                    )}
                </BottomSheet.Content>
                <BottomSheet.Content
                    header={{
                        onBackClick: closeSubItems,
                        title: "Create Pricebook Item",
                    }}
                    itemsClassName={styles.items}
                    priority="secondary"
                    isVisible={isSubItemsOpen}
                >
                    {(isTabbable) => (
                        <>
                            <BottomSheet.Item
                                path={pricebookServiceCreateRoute}
                                className={styles.bottomSheetItem}
                                label="Service"
                                isTabbable={isTabbable}
                            />

                            <BottomSheet.Item
                                path={pricebookPartOrMaterialCreateRoute}
                                className={styles.bottomSheetItem}
                                label="Part or Material"
                                isTabbable={isTabbable}
                            />

                            <BottomSheet.Item
                                path={pricebookMiscellaneousCreateRoute}
                                className={styles.bottomSheetItem}
                                label="Miscellaneous"
                                isTabbable={isTabbable}
                            />

                            <BottomSheet.Item
                                path={pricebookDiscountCreateRoute}
                                className={styles.bottomSheetItem}
                                label="Discount"
                                isTabbable={isTabbable}
                            />

                            {isUsingTaxRates && (
                                <BottomSheet.Item
                                    path={pricebookTaxRatesCreateRoute}
                                    className={styles.bottomSheetItem}
                                    label="Tax Rate"
                                    isTabbable={isTabbable}
                                />
                            )}
                        </>
                    )}
                </BottomSheet.Content>
            </BottomSheet.Root>
        </>
    )
}
